import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab } from 'Core/components';
import { AppModal } from '../components/app-modals';
import { addCipRegister, editCipRegister } from './store/slice';
import { CheckCircle } from 'react-feather';

const CIPRegisterAddEdit = ({ showModal, modalData, onCloseModal }) => {
  const dispatch = useDispatch();
  const isEditMode = Boolean(modalData?.id);

  const [form, setForm] = useState(modalData || {});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleClose = () => onCloseModal();

  const onSave = (status) => {
    const payload = { ...form, status: status || 'Draft' };
    if (isEditMode) {
      dispatch(editCipRegister(payload));
    } else {
      dispatch(addCipRegister(payload));
    }
    handleClose();
  };

  const Field = ({ label, name, value, col = "col-lg-3 col-md-4 col-sm-6", placeholder="N/A" }) => (
    <div className={col}>
      <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</label>
      <input
        type="text"
        className="form-control form-control-sm"
        name={name}
        placeholder={placeholder}
        value={value || ''}
        onChange={handleChange}
        style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', color: '#2d3748' }}
      />
    </div>
  );

  const TopCard = ({ label, value, valueColor = '#2d3748' }) => (
    <div className="col-md-3">
      <div className="p-2" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', minHeight: '64px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#718096', marginBottom: '2px', marginLeft: '4px' }}>{label}</div>
        <div style={{ fontSize: '1rem', fontWeight: 800, color: valueColor, marginLeft: '4px' }}>{value}</div>
      </div>
    </div>
  );

  const leftContent = () => (
    <div className="py-2">
      
      {/* Description */}
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem', color: '#718096' }}>Based on your uploaded Continuous Improvement Program template</p>

      {/* Top Cards */}
      <div className="row g-3 mb-4">
        <TopCard label="CIP #" value={form.cipNumber || modalData?.crNumber || 'CIP-2026-014'} valueColor="#2b6cb0" />
        <TopCard label="Source" value={form.source || 'Audit'} />
        <TopCard label="Coordinator" value={form.coordinator || 'Justin / Quality'} />
        <TopCard label="Due Date" value={form.dueDate || 'Mar 29, 2026'} />
      </div>

      {/* Tabs */}
      <Tabs>
        <Tab title="Overview">
          {/* Origination */}
          <h5 className="fw-bolder mb-3 mt-3" style={{ color: '#2d3748' }}>Origination</h5>
          <div className="row g-3 mb-4">
            <Field label="Originator" name="originator" value={form.originator} placeholder="Justin" />
            <Field label="Date" name="date" value={form.date} placeholder="02/02/2022" />
            <Field label="Department / Process" name="department" value={form.department} placeholder="Management" />
            <Field label="Customer Name" name="customerName" value={form.customerName} />
            <Field label="Customer Email" name="customerEmail" value={form.customerEmail} />
            <Field label="Customer CAR/NCR" name="customerCAR" value={form.customerCAR} />
            
            <Field label="Part Number" name="partNumber" value={form.partNumber} />
            <Field label="Part Revision" name="partRevision" value={form.partRevision} />
            <Field label="Quantity" name="quantity" value={form.quantity} />
            <Field label="Supplier Name" name="supplierName" value={form.supplierName} />
            <Field label="Supplier Email" name="supplierEmail" value={form.supplierEmail} />
            <Field label="Supplier P.O." name="supplierPO" value={form.supplierPO} />

            <div className="col-lg-6 col-md-4 d-none d-lg-block"></div> {/* Spacer to align next row like screenshot */}
            <Field label="Audit Number" name="auditNumber" value={form.auditNumber} placeholder="AUD-118" />
            <Field label="Document Name" name="documentName" value={form.documentName} placeholder="Doc Control Procedure" />
            <Field label="Standard" name="standard" value={form.standard} placeholder="ISO 9001:2015" />
          </div>

          {/* Improvement Opportunity */}
          <h5 className="fw-bolder mb-2" style={{ color: '#2d3748' }}>Improvement Opportunity</h5>
          <div className="mb-4">
            <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Opportunity Description</label>
            <textarea
              className="form-control"
              rows={3}
              name="opportunityDescription"
              placeholder="Improve document revision visibility and approval turnaround time across departments..."
              value={form.opportunityDescription || ''}
              onChange={handleChange}
              style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', color: '#2d3748' }}
            />
          </div>

        </Tab>
        <Tab title="Coordination & Validation">
          <div className="p-3 pt-4 mb-4 mt-3" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
            <h5 className="fw-bolder mb-3" style={{ color: '#2d3748' }}>Coordination</h5>
            <div className="row g-3 mb-4">
              <Field label="Coordinator Name" name="coordName" value={form.coordName} placeholder="Sojan" />
              <Field label="Date Coordinated" name="dateCoordinated" value={form.dateCoordinated} placeholder="03/16/2026" />
              <Field label="Classification" name="classification" value={form.classification} placeholder="Major" />
              <Field label="Result" name="result" value={form.result} placeholder="Accept" />
              <Field label="Investigation Assigned To" name="investigationAssignedTo" value={form.investigationAssignedTo} col="col-lg-4 col-md-6" placeholder="Engineering + Quality" />
              <div className="col-12 m-0 mt-1 d-none d-md-block"></div> {/* line break wrapper */}
              
              <Field label="Date Due" name="coordDateDue" value={form.coordDateDue} placeholder="03/29/2026" />
              <div className="col-lg-9 col-md-8">
                <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Containment Action</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="containmentAction"
                  placeholder="Temporary review checkpoint added before release of controlled documents."
                  value={form.containmentAction || ''}
                  onChange={handleChange}
                  style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', color: '#2d3748' }}
                />
              </div>
            </div>
          </div>

          <div className="p-3 pt-4 mb-0 mt-3" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
            <h5 className="fw-bolder mb-3" style={{ color: '#1a202c' }}>Validation</h5>
            <div className="row g-3">
              <Field label="Validator Name" name="validatorName" value={form.validatorName} placeholder="" col="col-lg-2 col-md-4" />
              <Field label="Date" name="valDate" value={form.valDate} placeholder="" col="col-lg-2 col-md-4" />
              <Field label="Action Effective" name="actionEffective" value={form.actionEffective} placeholder="" col="col-lg-2 col-md-4" />
              <Field label="Problem Did Not Reoccur" name="problemNotReoccur" value={form.problemNotReoccur} placeholder="" col="col-lg-3 col-md-6" />
              <Field label="Comments" name="valComments" value={form.valComments} placeholder="" col="col-lg-3 col-md-6" />
            </div>
          </div>
        </Tab>
        <Tab title="Correction Proposed">
          {/* Correction Proposed */}
          <div className="p-3 pt-4 mb-0 mt-3" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
            <h5 className="fw-bolder mb-3" style={{ color: '#1a202c' }}>Correction Proposed</h5>
            <div className="row g-3">
              <div className="col-lg-4">
                <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Correction Proposed</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="correctionProposed"
                  placeholder="Add stage-based workflow..."
                  value={form.correctionProposed || ''}
                  onChange={handleChange}
                  style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#2d3748' }}
                />
              </div>
              <div className="col-lg-3">
                <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Proposed Action Date</label>
                <input
                  type="text"
                  className="form-control"
                  name="proposedActionDate"
                  placeholder="03/22/2026"
                  value={form.proposedActionDate || ''}
                  onChange={handleChange}
                  style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#2d3748' }}
                />
              </div>
              <div className="col-lg-5">
                <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Root Cause</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="rootCause"
                  placeholder="Manual tracking and fragmented approvals..."
                  value={form.rootCause || ''}
                  onChange={handleChange}
                  style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#2d3748' }}
                />
              </div>
            </div>
          </div>
        </Tab>
        <Tab title="PDCA">
          <div className="text-center text-muted p-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px', backgroundColor: '#f8fafc', borderRadius: '16px' }}>
            <h4>PDCA Content Area <br/><span style={{fontSize: '1rem', fontWeight: 'normal'}}>This section is currently a placeholder for the UI Mockup.</span></h4>
          </div>
        </Tab>
        <Tab title="Approvals">
          <div className="p-3 pt-4 mb-0 mt-3" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bolder mb-0" style={{ color: '#f59e0b' }}>Approval Workflow</h5>
              <button 
                className="btn btn-sm btn-outline-warning" 
                style={{ borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, color: '#f59e0b', borderColor: '#f59e0b' }}
              >
                + Add Approver
              </button>
            </div>

            <div className="d-flex gap-2 mb-4">
              <span className="badge" style={{ backgroundColor: '#28a745', padding: '8px 16px', borderRadius: '4px', fontSize: '0.85rem' }}>3 Approved</span>
              <span className="badge" style={{ backgroundColor: '#ffc107', padding: '8px 16px', borderRadius: '4px', fontSize: '0.85rem' }}>1 Pending</span>
              <span className="badge" style={{ backgroundColor: '#dc3545', padding: '8px 16px', borderRadius: '4px', fontSize: '0.85rem' }}>0 Rejected</span>
              <span className="badge" style={{ backgroundColor: '#17a2b8', padding: '8px 16px', borderRadius: '4px', fontSize: '0.85rem' }}>0 In Review</span>
            </div>

            <div className="table-responsive border rounded" style={{ borderColor: '#e2e8f0' }}>
              <table className="table table-bordered mb-0" style={{ fontSize: '0.9rem' }}>
                <thead className="bg-light">
                  <tr>
                    <th width="5%" className="py-2 text-center">#</th>
                    <th width="35%" className="py-2">Department / Approver</th>
                    <th width="20%" className="py-2">Status</th>
                    <th width="40%" className="py-2">Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { id: 1, dept: 'Quality Assurance', status: 'Approved', comments: 'Optional comments', color: '#28a745' },
                    { id: 2, dept: 'IT Department', status: 'Approved', comments: 'Optional comments', color: '#28a745' },
                    { id: 3, dept: 'Operations', status: 'Pending', comments: 'Optional comments', color: '#ffc107' },
                    { id: 4, dept: 'Finance', status: 'Approved', comments: 'Optional comments', color: '#28a745' }
                  ].map((row) => (
                    <tr key={row.id}>
                      <td className="text-center align-middle" style={{ color: '#718096' }}>{row.id}</td>
                      <td>
                        <input 
                          type="text" 
                          className="form-control form-control-sm bg-light" 
                          readOnly 
                          value={row.dept} 
                          style={{ borderRadius: '6px', border: '1px solid #e2e8f0', color: '#718096' }} 
                        />
                      </td>
                      <td>
                        <select 
                          className="form-select form-select-sm" 
                          value={row.status} 
                          style={{ 
                            borderRadius: '6px', 
                            border: '1px solid #e2e8f0', 
                            color: row.color, 
                            fontWeight: 700 
                          }}
                        >
                          <option value="Approved">Approved</option>
                          <option value="Pending">Pending</option>
                          <option value="Rejected">Rejected</option>
                          <option value="In Review">In Review</option>
                        </select>
                      </td>
                      <td>
                        <input 
                          type="text" 
                          className="form-control form-control-sm" 
                          placeholder={row.comments} 
                          style={{ borderRadius: '6px', border: '1px solid #e2e8f0' }} 
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
        <Tab title="Verification">
          <div className="p-3 pt-4 mb-0 mt-3" style={{ backgroundColor: '#fff', borderRadius: '16px' }}>
            <h5 className="fw-bolder mb-3" style={{ color: '#2d3748' }}>Verification Details</h5>
            <div className="row g-3">
              <Field label="Verifier Name" name="verifierName" value={form.verifierName} placeholder="Sojan" col="col-lg-4 col-md-6" />
              <Field label="Root Cause Addressed" name="rootCauseAddressed" value={form.rootCauseAddressed} placeholder="Yes" col="col-lg-4 col-md-6" />
              <Field label="Action Completed" name="actionCompleted" value={form.actionCompleted} placeholder="No" col="col-lg-4 col-md-6" />
              <div className="col-12">
                <label className="form-label mb-1" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#718096' }}>Verification Comments</label>
                <textarea 
                  className="form-control" 
                  rows={4} 
                  name="verificationComments" 
                  placeholder="Awaiting operations sign-off before final closure..." 
                  value={form.verificationComments || ''} 
                  onChange={handleChange} 
                  style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', color: '#2d3748', resize: 'none' }} 
                />
              </div>
            </div>
          </div>
        </Tab>
        <Tab title="History">
          <div className="text-center text-muted p-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px', backgroundColor: '#f8fafc', borderRadius: '16px' }}>
            <h4>History Content Area <br/><span style={{fontSize: '1rem', fontWeight: 'normal'}}>This section is currently a placeholder for the UI Mockup.</span></h4>
          </div>
        </Tab>
      </Tabs>
    </div>
  );

  const pdcaHeaderProgress = () => (
    <div className="d-flex align-items-center me-auto">
      <span className="fw-bold me-3 text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PDCA Progress:</span>
      <div className="d-flex gap-4">
        {['Plan', 'Do', 'Check', 'Act'].map((step, i) => (
          <div key={step} className="d-flex align-items-center">
            <div style={{
              width: 22, height: 22, borderRadius: '50%', 
              backgroundColor: i < 2 ? '#2b6cb0' : '#e2e8f0', 
              color: i < 2 ? '#fff' : '#a0aec0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', fontWeight: 'bold',
              flexShrink: 0
            }}>
              {i + 1}
            </div>
            <div className="ms-2">
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: i < 2 ? '#2b6cb0' : '#718096', lineHeight: 1 }}>{step}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const actionButtons = () => (
    <div className="d-flex gap-2">
      <button type="button" onClick={() => onSave('Draft')} className="btn btn-sm" style={{ backgroundColor: '#6c757d', color: '#fff' }}>Save Draft</button>
      <button type="button" onClick={() => onSave('In Review')} className="btn btn-sm btn-primary">Submit for Review</button>
      <button type="button" onClick={() => onSave('Approved')} className="btn btn-sm btn-success">Approve</button>
      <button type="button" onClick={() => onSave('Rejected')} className="btn btn-sm btn-danger">Reject</button>
    </div>
  );

  const body = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Static Top Header with PDCA Progress and Action Buttons ── */}
      <div className="d-flex align-items-center pb-3 mb-2" style={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        {pdcaHeaderProgress()}
        {actionButtons()}
      </div>

      {/* ── Scrollable content area ── */}
      <div className="row g-0" style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: '4px' }}>
        <div className="col-12 pb-4">
          {leftContent()}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      id="cip-register-modal"
      show={showModal}
      size="xl"
      dialogClassName="modal-fullscreen-lg-down modal-90w cr-fit-modal"
      title={isEditMode ? `Edit CIP: ${modalData?.cipNumber || modalData?.crNumber}` : 'CIP Form - UI/UX Mockup'}
      body={body}
      handleClose={handleClose}
      style={{ maxHeight: '95vh' }}
    />
  );
};

CIPRegisterAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.object,
  onCloseModal: PropTypes.func,
};

export default AppModal(CIPRegisterAddEdit);
